import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import { Card } from '../ui/Card';
import { useSidebarPathAccess } from '../../hooks/useSidebarPathAccess';

interface RequireRoleProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
}

export const RequireRole: React.FC<RequireRoleProps> = ({ children, allowedRoles }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const { loading: sidebarLoading, granted: sidebarGranted } = useSidebarPathAccess();

  if (!currentUser?.roles) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const hasRequiredRole = currentUser.roles.some((role) => allowedRoles.includes(role as UserRole));

  if (hasRequiredRole || sidebarGranted) {
    return <>{children}</>;
  }

  if (sidebarLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-gray-400 text-sm">Checking access…</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
        <p className="text-gray-400 mb-4">
          You don't have permission to access this page. Required roles: {allowedRoles.join(', ')}
        </p>
        <button
          onClick={() => window.history.back()}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
        >
          Go Back
        </button>
      </Card>
    </div>
  );
};
